import { create } from 'zustand';
import { apiService } from '../services/api';
import type { PlanTier } from '../utils/planTiers';

// Types matching backend models
export interface SubscriptionPlan {
  id: string;
  planId: string;
  name: string;
  tier: PlanTier;
  description?: string;
  /** Owning enterprise. Null means platform-global: assignable by every enterprise, editable by none. */
  enterpriseId?: string | null;
  /** Supplied by the list endpoint only. Null for a platform-global plan. */
  enterpriseName?: string | null;
  isPublic: boolean;
  isActive: boolean;
  included: {
    encountersPerMonth: number; // -1 = unlimited (legacy: org-level)
    transcriptionMinutes: number; // -1 = unlimited (legacy: org-level)
    users: number;
    storageGB: number;
    // Per-clinical-user limits (new model)
    encountersPerClinicalUser?: number;    // e.g., 200 encounters per clinical user
    minutesPerClinicalUser?: number;       // e.g., 500 minutes per clinical user
    staffUsersIncluded?: number;           // e.g., 5 staff users at no extra cost
  };
  pricing: {
    // Legacy flat pricing (deprecated but kept for backward compat)
    basePrice?: number;
    currency: string;
    billingCycle: 'monthly' | 'quarterly' | 'annual';
    perUserPrice?: number;          // Deprecated: use monthlyPerClinicalUser
    perStaffUserPrice?: number;     // Deprecated: use monthlyPerStaffUser

    // New per-user pricing model
    setupFeePerClinicalUser?: number;     // One-time fee when clinical user is added (e.g., $99)
    monthlyPerClinicalUser?: number;      // Monthly recurring per clinical user (e.g., $49)
    monthlyPerStaffUser?: number;         // Monthly recurring per staff user (e.g., $10)
  };
  defaultOverages: {
    allowOverages: boolean;
    encounterOveragePrice: number;
    minuteOveragePrice: number;
    maxOverageLimit: number;
  };
  features: {
    enableClinicalCoding: boolean;
    enableCustomTemplates: boolean;
    enableAdvancedAnalytics: boolean;
    enableApiAccess: boolean;
    enablePrioritySupport: boolean;
    maxConcurrentRecordings: number;
  };
}

export interface UsageMetrics {
  encounters: {
    used: number;
    limit: number;
    overage: number;
    percentUsed: number;
    isUnlimited: boolean;
  };
  minutes: {
    used: number;
    limit: number;
    overage: number;
    percentUsed: number;
    isUnlimited: boolean;
  };
  isInOverage: boolean;
  currentOverageAmount: number;
  shouldBlock: boolean;
  blockReason?: string;
}

export interface Subscription {
  id: string;
  organizationId: string;
  organizationName?: string;
  planId?: string;
  planName?: string;
  status: 'active' | 'trialing' | 'past_due' | 'suspended' | 'cancelled' | 'expired';
  period: {
    currentPeriodStart: Date;
    currentPeriodEnd: Date;
    trialEndsAt?: Date;
  };
  usage: {
    currentEncountersThisMonth: number;
    currentTranscriptionMinutes: number;
    currentUsers?: number;
    lastUsageUpdate?: Date;
  };
  limits: {
    maxEncountersPerMonth: number;
    maxTranscriptionMinutes: number;
    maxUsers: number;
    maxStorageGB: number;
    allowOverages: boolean;
  };
  overageTracking: {
    encounterOverageCount: number;
    minuteOverageCount: number;
    currentOverageAmount: number;
    lastNotificationLevel: 'none' | '80' | '90' | '100' | 'max';
  };
  // Per-clinical-user model fields
  userCounts?: {
    clinical: number;       // Billable users (with recording permissions)
    staff: number;          // Non-billable users
    total: number;
    lastSyncedAt?: Date;
  };
  effectiveLimits?: {
    totalEncountersThisMonth: number;   // = clinicalUsers × encountersPerClinicalUser
    totalMinutesThisMonth: number;
    calculatedAt?: Date;
  };
  // Plan reference for per-user calculations
  plan?: {
    encountersPerClinicalUser?: number;
    minutesPerClinicalUser?: number;
  };
}

/** Mirrors the API's `UserUsageBreakdown`. Fields optional here are optional there. */
export interface UserUsageBreakdown {
  userId: string;
  userName?: string;
  email?: string;
  /** False when the backend could not resolve the user record. The usage is still real. */
  identityResolved: boolean;
  encounters: number;
  minutes: number;
  percentOfOrgTotal: number;
  isHighUsage: boolean;
  hasOverages: boolean;
  lastActivityAt?: string;
}

export interface PeriodUsage {
  periodKey: string;
  periodStart: Date;
  periodEnd: Date;
  encounters: number;
  minutes: number;
  overageAmount: number;
}

export interface UsageAlert {
  level: '80' | '90' | '100' | 'max';
  type: 'encounters' | 'minutes' | 'both';
  message: string;
  percentUsed: number;
  timestamp: Date;
}

export interface PersonalUsage {
  user: { userId: string; userName: string; email: string };
  currentPeriod: { start: Date; end: Date; periodKey: string };
  myUsage: {
    encounters: number;
    minutes: number;
    percentOfOrgTotal: number;
    // Per-user limit fields
    personalEncounterLimit: number;      // -1 = unlimited
    personalMinuteLimit: number;
    personalEncounterRemaining: number;
    personalEncounterPercentUsed: number;
    isOverPersonalLimit: boolean;
    proratedFraction: number;
    overagesAllowed: boolean;
    overageEncounters: number;
    overagePrice: number;
  };
  orgLimits: {
    encountersPerMonth: number;
    transcriptionMinutes: number;
    isUnlimited: { encounters: boolean; minutes: boolean };
  };
  orgUsage: {
    encountersUsed: number;
    encountersPercent: number;
    minutesUsed: number;
    minutesPercent: number;
    isInOverage: boolean;
  };
  recentActivity: Array<{
    encounterId: string;
    timestamp: Date;
    minutes: number;
    wasOverage: boolean;
  }>;
}

interface SubscriptionState {
  // Plans catalog (platform/superadmin)
  plans: SubscriptionPlan[];
  plansLoading: boolean;
  plansError: string | null;

  // Current org subscription and usage
  subscription: Subscription | null;
  usage: UsageMetrics | null;
  usageLoading: boolean;
  usageError: string | null;

  // User breakdown
  userUsage: UserUsageBreakdown[];
  userUsageLoading: boolean;

  // Historical usage
  usageHistory: PeriodUsage[];
  historyLoading: boolean;

  // Alerts
  alerts: UsageAlert[];

  // Personal usage (operator/staff view)
  personalUsage: PersonalUsage | null;
  personalUsageLoading: boolean;

  // All subscriptions (platform view)
  allSubscriptions: Subscription[];
  allSubscriptionsLoading: boolean;

  // Actions - Plans (superadmin)
  fetchPlans: () => Promise<void>;
  createPlan: (plan: Partial<SubscriptionPlan>) => Promise<SubscriptionPlan>;
  updatePlan: (planId: string, updates: Partial<SubscriptionPlan>) => Promise<SubscriptionPlan>;
  deletePlan: (planId: string) => Promise<void>;

  // Actions - Current org subscription
  fetchSubscription: () => Promise<void>;
  fetchUsage: () => Promise<void>;
  fetchUserUsage: () => Promise<void>;
  fetchUsageHistory: (periods?: number) => Promise<void>;
  fetchAlerts: () => Promise<void>;
  fetchPersonalUsage: () => Promise<void>;

  // Actions - Platform (superadmin)
  fetchAllSubscriptions: () => Promise<void>;
  assignPlanToOrg: (orgId: string, planId: string) => Promise<void>;
  updateOrgOverages: (orgId: string, overageLimits: any) => Promise<void>;
  bulkAssignPlan: (organizationIds: string[], planId: string) => Promise<BulkAssignResult>;

  // Reset
  clearStore: () => void;
}

// Result type for bulk assignment
export interface BulkAssignResult {
  success: string[];
  failed: { orgId: string; error: string }[];
}

export const useSubscriptionStore = create<SubscriptionState>()((set, get) => ({
  // Initial state
  plans: [],
  plansLoading: false,
  plansError: null,
  subscription: null,
  usage: null,
  usageLoading: false,
  usageError: null,
  userUsage: [],
  userUsageLoading: false,
  usageHistory: [],
  historyLoading: false,
  alerts: [],
  personalUsage: null,
  personalUsageLoading: false,
  allSubscriptions: [],
  allSubscriptionsLoading: false,

  // Fetch plans catalog
  fetchPlans: async () => {
    set({ plansLoading: true, plansError: null });
    try {
      const response = await apiService.get<{ success: boolean; data: SubscriptionPlan[] }>(
        '/v1/platform/subscription-plans'
      );
      set({ plans: response.data, plansLoading: false });
    } catch (error: any) {
      set({
        plansError: error.response?.data?.error || 'Failed to fetch plans',
        plansLoading: false
      });
    }
  },

  // Create a new plan
  createPlan: async (plan) => {
    const response = await apiService.post<{ success: boolean; data: SubscriptionPlan }>(
      '/v1/platform/subscription-plans',
      plan
    );
    const newPlan = response.data;
    set((state) => ({ plans: [...state.plans, newPlan] }));
    return newPlan;
  },

  // Update a plan
  updatePlan: async (planId, updates) => {
    const response = await apiService.put<{ success: boolean; data: SubscriptionPlan }>(
      `/v1/platform/subscription-plans/${planId}`,
      updates
    );
    const updatedPlan = response.data;
    set((state) => ({
      plans: state.plans.map((p) => (p.planId === planId ? updatedPlan : p))
    }));
    return updatedPlan;
  },

  // Delete a plan
  deletePlan: async (planId) => {
    await apiService.delete(`/v1/platform/subscription-plans/${planId}`);
    set((state) => ({
      plans: state.plans.filter((p) => p.planId !== planId)
    }));
  },

  // Fetch current organization's subscription
  fetchSubscription: async () => {
    set({ usageLoading: true, usageError: null });
    try {
      const response = await apiService.get<{ success: boolean; data: any }>(
        '/v1/organization/subscription'
      );
      const d = response.data;
      // Map API response: plan.name → planName for SubscriptionStatusBar
      const subscription: Subscription = {
        ...d,
        planName: d.planName || d.plan?.name || undefined,
        planId: d.planId || d.plan?.tier || undefined,
      };
      set({ subscription, usageLoading: false });
    } catch (error: any) {
      set({
        usageError: error.response?.data?.error || 'Failed to fetch subscription',
        usageLoading: false
      });
    }
  },

  // Fetch current usage metrics
  fetchUsage: async () => {
    set({ usageLoading: true, usageError: null });
    try {
      const response = await apiService.get<{ success: boolean; data: any }>(
        '/v1/organization/subscription/usage'
      );
      const d = response.data;
      // Map API response shape to UsageMetrics interface
      const usage: UsageMetrics = {
        encounters: {
          used: d.encounters?.used ?? 0,
          limit: d.encounters?.limit ?? 0,
          overage: d.encounters?.overageCount ?? 0,
          percentUsed: d.encounters?.percentUsed ?? 0,
          isUnlimited: d.encounters?.limit === -1,
        },
        minutes: {
          used: d.transcriptionMinutes?.used ?? d.minutes?.used ?? 0,
          limit: d.transcriptionMinutes?.limit ?? d.minutes?.limit ?? 0,
          overage: d.transcriptionMinutes?.overageMinutes ?? d.minutes?.overage ?? 0,
          percentUsed: d.transcriptionMinutes?.percentUsed ?? d.minutes?.percentUsed ?? 0,
          isUnlimited: (d.transcriptionMinutes?.limit ?? d.minutes?.limit) === -1,
        },
        isInOverage: d.overage?.isInOverage ?? d.isInOverage ?? false,
        currentOverageAmount: d.overage?.totalAmount ?? d.currentOverageAmount ?? 0,
        shouldBlock: d.shouldBlock ?? false,
        blockReason: d.blockReason,
      };
      set({ usage, usageLoading: false });
    } catch (error: any) {
      set({
        usageError: error.response?.data?.error || 'Failed to fetch usage',
        usageLoading: false
      });
    }
  },

  // Fetch usage breakdown by user
  fetchUserUsage: async () => {
    set({ userUsageLoading: true });
    try {
      const response = await apiService.get<{ success: boolean; data: UserUsageBreakdown[] }>(
        '/v1/organization/subscription/usage/by-user'
      );
      set({ userUsage: response.data, userUsageLoading: false });
    } catch (error) {
      set({ userUsageLoading: false });
    }
  },

  // Fetch historical usage
  fetchUsageHistory: async (periods = 6) => {
    set({ historyLoading: true });
    try {
      const response = await apiService.get<{ success: boolean; data: PeriodUsage[] }>(
        '/v1/organization/subscription/usage/history',
        { periods }
      );
      set({ usageHistory: response.data, historyLoading: false });
    } catch (error) {
      set({ historyLoading: false });
    }
  },

  // Fetch alerts
  fetchAlerts: async () => {
    try {
      const response = await apiService.get<{ success: boolean; data: UsageAlert[] }>(
        '/v1/organization/subscription/alerts'
      );
      set({ alerts: response.data });
    } catch (error) {
      // Silent fail for alerts
    }
  },

  // Fetch personal usage (for operator/staff)
  fetchPersonalUsage: async () => {
    set({ personalUsageLoading: true });
    try {
      const response = await apiService.get<{ success: boolean; data: PersonalUsage }>(
        '/v1/organization/subscription/usage/me'
      );
      set({ personalUsage: response.data, personalUsageLoading: false });
    } catch (error) {
      set({ personalUsageLoading: false, personalUsage: null });
    }
  },

  // Fetch all subscriptions (platform view)
  fetchAllSubscriptions: async () => {
    set({ allSubscriptionsLoading: true });
    try {
      // Backend returns enriched data: { subscription, organization, usageStatus }[]
      interface BackendSubscriptionItem {
        subscription: {
          id: string;
          organizationId: string;
          planId?: string;
          plan?: { name: string; tier: string };
          status: 'active' | 'trialing' | 'past_due' | 'suspended' | 'cancelled' | 'expired';
          included: {
            encountersPerMonth: number;
            transcriptionMinutes: number;
            users: number;
            storageGB: number;
          };
          usage: {
            currentEncountersThisMonth: number;
            currentTranscriptionMinutes: number;
            currentUsers: number;
            lastUpdated?: Date;
          };
          overages: {
            allowOverages: boolean;
          };
          overageTracking?: {
            encounterOverageCount: number;
            minuteOverageCount: number;
            currentOverageAmount: number;
            lastNotificationLevel: 'none' | '80' | '90' | '100' | 'max';
          };
          period: {
            currentPeriodStart: Date;
            currentPeriodEnd: Date;
            trialEndsAt?: Date;
          };
        };
        organization: {
          id: string;
          name: string;
          subdomain?: string;
        } | null;
        usageStatus: {
          encountersUsed: number;
          encountersLimit: number;
          encountersPercent: number;
          minutesUsed: number;
          minutesLimit: number;
          minutesPercent: number;
          usersActive: number;
          usersLimit: number;
          overageStatus: 'normal' | 'approaching' | 'over';
          currentOverageAmount: number;
        };
      }

      const response = await apiService.get<{ success: boolean; data: BackendSubscriptionItem[] }>(
        '/v1/platform/subscriptions'
      );

      // Transform backend format to frontend Subscription interface
      const subscriptions: Subscription[] = response.data.map((item) => ({
        id: item.subscription.id,
        organizationId: item.subscription.organizationId,
        organizationName: item.organization?.name || 'Unknown Organization',
        planId: item.subscription.planId,
        planName: item.subscription.plan?.name || 'No Plan',
        status: item.subscription.status,
        period: {
          currentPeriodStart: item.subscription.period.currentPeriodStart,
          currentPeriodEnd: item.subscription.period.currentPeriodEnd,
          trialEndsAt: item.subscription.period.trialEndsAt
        },
        usage: {
          currentEncountersThisMonth: item.subscription.usage.currentEncountersThisMonth,
          currentTranscriptionMinutes: item.subscription.usage.currentTranscriptionMinutes,
          lastUsageUpdate: item.subscription.usage.lastUpdated
        },
        limits: {
          maxEncountersPerMonth: item.subscription.included.encountersPerMonth,
          maxTranscriptionMinutes: item.subscription.included.transcriptionMinutes,
          maxUsers: item.subscription.included.users,
          maxStorageGB: item.subscription.included.storageGB,
          allowOverages: item.subscription.overages.allowOverages
        },
        overageTracking: {
          encounterOverageCount: item.subscription.overageTracking?.encounterOverageCount ?? 0,
          minuteOverageCount: item.subscription.overageTracking?.minuteOverageCount ?? 0,
          currentOverageAmount: item.subscription.overageTracking?.currentOverageAmount ?? 0,
          lastNotificationLevel: item.subscription.overageTracking?.lastNotificationLevel ?? 'none'
        }
      }));

      set({ allSubscriptions: subscriptions, allSubscriptionsLoading: false });
    } catch (error) {
      set({ allSubscriptionsLoading: false });
    }
  },

  // Assign a plan to an organization
  assignPlanToOrg: async (orgId, planId) => {
    await apiService.post(`/v1/platform/subscriptions/${orgId}/assign`, { planId });
    // Refresh all subscriptions
    get().fetchAllSubscriptions();
  },

  // Update organization overage limits
  updateOrgOverages: async (orgId, overageLimits) => {
    await apiService.put(`/v1/platform/subscriptions/${orgId}/overages`, overageLimits);
    get().fetchAllSubscriptions();
  },

  // Bulk assign plan to multiple organizations
  bulkAssignPlan: async (organizationIds, planId) => {
    const response = await apiService.post<{ success: boolean; data: BulkAssignResult }>(
      '/v1/platform/subscriptions/direct-assign',
      { organizationIds, planId }
    );
    // Refresh all subscriptions
    get().fetchAllSubscriptions();
    return response.data;
  },

  // Clear store
  clearStore: () => {
    set({
      plans: [],
      plansLoading: false,
      plansError: null,
      subscription: null,
      usage: null,
      usageLoading: false,
      usageError: null,
      userUsage: [],
      userUsageLoading: false,
      usageHistory: [],
      historyLoading: false,
      alerts: [],
      personalUsage: null,
      personalUsageLoading: false,
      allSubscriptions: [],
      allSubscriptionsLoading: false
    });
  }
}));

export default useSubscriptionStore;
